

import { type KeyboardEventHandler, type SyntheticEvent, useMemo } from 'react';

interface UseDisabledStatePropsInterface {
  /**
   * Whether the component is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the component should be keyboard focusable when disabled
   * @default true
   */
  focusable?: boolean;
}

// For mouse, touch, and focus events
const noopEventHandler = (event: SyntheticEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

// For keyboard events
// Only prevents events that would trigger a button press.
// Ex: Don't prevent people from using tab or arrow keys to navigate away.
const noopKeyboardEventHandler: KeyboardEventHandler<HTMLElement> = (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.stopPropagation();
  }
};

/**
 * Hook that returns accessibility-compliant props for disabling interactive components.
 * Provides a more accessible alternative to the native `disabled` attribute by using
 * `aria-disabled` and preventing all interactive events while maintaining focus behavior.
 *
 * Note that props returned should be spread after the component's rest props to
 * prevent accidental overrides.
 *
 * If supporting a component that can be rendered polymorphically as an anchor,
 * you can also pass these props to the `use-disabled-anchor` hook.
 *
 * @param {object} props - Configuration object for disabled state.
 * @param {boolean} [props.disabled=false] - Whether the component is disabled.
 * @param {boolean} [props.focusable=false] - Whether the component should remain keyboard focusable when disabled.
 * @returns {object} Object containing accessibility props and event handlers for disabled state, or empty object if not disabled.
 *
 * @see https://www.scottohara.me/blog/2021/05/28/disabled-links.html
 *
 * @example
 * const { disabled } = props;
 * const a11yDisabledState = useDisabledState({ disabled, focusable: true });
 * return (
 *  <Component
 *    {...rest}
 *    {...a11yDisabledState}
 *  />
 * );
 */
export const useDisabledState = ({
  disabled = false,
  focusable = false,
}: UseDisabledStatePropsInterface) => {
  return useMemo(
    () =>
      disabled
        ? {
            'aria-disabled': true,
            'onClick': noopEventHandler,
            'onDoubleClick': noopEventHandler,
            'onFocus': noopEventHandler,
            'onKeyDown': noopKeyboardEventHandler,
            'onKeyPress': noopKeyboardEventHandler,
            'onKeyUp': noopKeyboardEventHandler,
            'onMouseDown': noopEventHandler,
            'onMouseUp': noopEventHandler,
            'onPointerDown': noopEventHandler,
            'onPointerUp': noopEventHandler,
            'onTouchEnd': noopEventHandler,
            'onTouchMove': noopEventHandler,
            'onTouchStart': noopEventHandler,
            'tabIndex': focusable ? 0 : -1,
          }
        : {},
    [disabled, focusable],
  );
};
